import { Injectable } from '@angular/core';
import { ProcedureEnum } from '@core/procedure-helpers/models/procedure.enum';
import { OrderQuery } from '@modules/order/state/order-query';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { TeethDiagramQuery } from '@modules/teeth-diagram/state/teeth-diagram-query';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { combineLatest, filter, Observable } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';

import {
	getToothNumberById,
	IntuitiveToothForTreatment,
	DiagramToothAndBridgeListViewItem,
	TeethNumberingSystem,
	compareTreatmentListItems
} from '../models';
import { IntuitiveBridge } from '../models/intuitive-bridge';
import { IntuitiveIndication } from '../models/intuitive-indication';
import { RxIntuitiveBridgeService } from './rx-intuitive-bridge-service';
import { RxIntuitiveTeethDiagramQuery } from '../state/rx-intuitive-teeth-diagram-query';
import { ValidateRxService } from '@shared/services/validate-rx';
import { RxIntuitiveIndicationMapperService } from './rx-intuitive-indication-mapper.service';
import { getTeethNumberingSystem } from '@shared/utils/teeth-numbering-system-util';
import { RoleTypeEnum } from '@shared/models/role-type';
import { LoggerService } from '@core/services/logger/logger.service';
import { TeethDiagramStore } from '@modules/teeth-diagram/state/teeth-diagram-store';
import { isIndicationWithModals } from '@modules/rx-intuitive-teeth-orchestrator/services/rx-intuitive-tooth-editor-roter.service';
import { RxIntuitiveToothTreatmentMapper } from './rx-intuitive-tooth-treatment-mapper.service';
import { buildTreatmentPreviewLine, buildBridgePreviewLine } from '../utils/build-treatment-preview-line';
import { buildTreatmentListPreviewTitle, normalizeImplantsSummaryLine } from '../utils/build-treatment-list-preview-title';
import { RxTemplateService } from '@shared/rx-templates/rx-template.service';

@Injectable()
export class RxIntuitiveTeethHelperService {
	readonly teethNumberingSystem$: Observable<TeethNumberingSystem> = combineLatest({
		userSettings: this.shellQuery.userSettings$,
		rx: this.shellQuery.rx$,
		forceDisplayTeethNumberingSystem: this.teethDiagramQuery.forceDisplayTeethNumberingSystem$
	}).pipe(
		map(({ userSettings, rx, forceDisplayTeethNumberingSystem }) =>
			getTeethNumberingSystem({
				forceTeethNumberingSystem: forceDisplayTeethNumberingSystem,
				teethNumberingSystemFromUserSettings: userSettings?.ToothId,
				teethNumberingSystemFromRx: rx?.ToothNumberingSystem,
				isDoctor: this.shellQuery.userRole === RoleTypeEnum.Doctor,
				logger: this.logger
			})
		),
		tap(system => {
			this.teethDiagramStore.update({ teethNumberingSystem: system });
			// `teethNumberingSystem` is a display preference, not user work. Re-baseline the dirty check
			// after writing it so the whole-store isDirty does not flag an untouched diagram as dirty
			// (which would wrongly trigger the send-to-change reset confirmation).
			this.teethDiagramQuery.resetDirtyCheckHead();
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly upperJawBridges$: Observable<Record<number, IntuitiveBridge>> = this.teethDiagramQuery.upperJaw$.pipe(
		map(jaw => this.bridgeService.getAllBridgesForJawIntuitive(jaw)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly lowerJawBridges$: Observable<Record<number, IntuitiveBridge>> = this.teethDiagramQuery.lowerJaw$.pipe(
		map(jaw => this.bridgeService.getAllBridgesForJawIntuitive(jaw)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly bridges$: Observable<Record<number, IntuitiveBridge>> = combineLatest([this.upperJawBridges$, this.lowerJawBridges$]).pipe(
		map(([upperJawBridges, lowerJawBridges]) => ({ ...upperJawBridges, ...lowerJawBridges })),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly selectedTeeth$: Observable<Tooth[]> = combineLatest([
		this.teethDiagramQuery.teethRecord$,
		this.intuitiveQuery.selectedToothIds$
	]).pipe(
		map(([teethRecord, selectedToothIds]) => this.getTeethByIds(selectedToothIds, teethRecord)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly selectedBridgeNonNull$: Observable<IntuitiveBridge> = this.intuitiveQuery.selectedBridge$.pipe(
		filter((bridge): bridge is IntuitiveBridge => bridge != null)
	);

	readonly selectedBridgeTeeth$: Observable<Tooth[]> = this.intuitiveQuery.selectedBridgeTeeth$;

	/**
	 * Whether the side tooth editor should be visible (selected teeth have non-default, non-modal indications).
	 */
	readonly isToothEditorShown$: Observable<boolean> = combineLatest([this.selectedTeeth$, this.intuitiveQuery.selectedToothIds$]).pipe(
		map(([selectedTeeth, selectedToothIds]) => {
			if (selectedToothIds.length === 0) {
				return false;
			}

			const nonDefault = selectedTeeth.filter(t => !this.indicationMapper.isDefaultUnitType(t.UnitTypeID));

			if (nonDefault.length === 0) {
				return false;
			}

			const indicationId = this.indicationMapper.getUnitTypeIndicationForDiagram(nonDefault[0].UnitTypeID);

			// The side tooth editor is shown for non-default indications, except those routed to a
			// modal dialog instead (e.g. implant-based). When modals are suppressed by the template,
			// such indications fall back to the side editor.
			const opensInModal = isIndicationWithModals(indicationId, this.areImplantModalsSuppressed);

			return !opensInModal;
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	/**
	 * Non-default teeth mapped to preview view models, sorted by lastTouchedAt (desc), tie-break by toothId (asc).
	 * Excludes teeth that belong to a bridge (they appear as bridge summary rows instead).
	 */
	readonly previewTeeth$: Observable<IntuitiveToothForTreatment[]> = combineLatest([
		this.teethDiagramQuery.teethFlat$,
		this.teethNumberingSystem$,
		this.intuitiveQuery.toothLastTouchedAt$
	]).pipe(
		map(([teethFlat, teethNumberingSystem, toothLastTouchedAt]) => {
			return teethFlat
				.filter(t => {
					if (t.ToothID == null) {
						return false;
					}
					if (t.BridgeIndex != null) {
						return false;
					}

					return !this.indicationMapper.isDefaultUnitType(t.UnitTypeID);
				})
				.map(t => this.toothTreatmentMapper.toIntuitiveToothForTreatment(t, teethNumberingSystem, toothLastTouchedAt, false))
				.sort((a, b) => {
					if (b.lastTouchedAt !== a.lastTouchedAt) {
						return b.lastTouchedAt - a.lastTouchedAt;
					}

					return a.toothId - b.toothId;
				});
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	/**
	 * Treatment list items: tooth preview rows merged with bridge summary rows, sorted by time then toothId.
	 */
	readonly previewTreatmentListItems$: Observable<DiagramToothAndBridgeListViewItem[]> = combineLatest([
		this.previewTeeth$,
		this.bridges$,
		this.teethNumberingSystem$,
		this.intuitiveQuery.toothLastTouchedAt$,
		this.orderQuery.procedureMap$
	]).pipe(
		map(([previewTeeth, bridges, teethNumberingSystem, toothLastTouchedAt, procedureMap]) => {
			const toothRows: DiagramToothAndBridgeListViewItem[] = previewTeeth.map(t => {
				const previewLine = buildTreatmentPreviewLine(t);
				const implantsSummaryLine = normalizeImplantsSummaryLine(t.implantsTreatmentSummary);

				return {
					startToothId: t.toothId,
					previewLine,
					previewTitle: buildTreatmentListPreviewTitle({ indicationId: t.indicationId, previewLine, implantsSummaryLine }),
					implantsSummaryLine,
					lastTouchedAt: t.lastTouchedAt,
					indicationId: t.indicationId,
					startToothNumberInSystem: t.toothNumberInSystem,
					isInvalid: this.shouldValidateForSend ? this.validateToothForSend(this.teethDiagramQuery.teethRecord[t.toothId]) : false
				};
			});

			if ((procedureMap?.ProcedureId as ProcedureEnum) !== ProcedureEnum.FixedRestorative) {
				return toothRows;
			}

			const bridgeRows: DiagramToothAndBridgeListViewItem[] = Object.values(bridges ?? {}).map(bridge => {
				return this.mapBridgeToDiagramListItem(bridge, teethNumberingSystem, toothLastTouchedAt);
			});

			return [...toothRows, ...bridgeRows].sort(compareTreatmentListItems);
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	mapBridgeToDiagramListItem(
		bridge: IntuitiveBridge,
		teethNumberingSystem: TeethNumberingSystem,
		toothLastTouchedAt: Record<number, number>
	): DiagramToothAndBridgeListViewItem {
		const bridgeTeeth = bridge.teeth.map(tooth =>
			this.toothTreatmentMapper.toIntuitiveToothForTreatment(tooth, teethNumberingSystem, toothLastTouchedAt, true)
		);

		const isInvalid = bridge.teeth.some(tooth => this.validateToothForSend(tooth, bridge));
		const previewLine = buildBridgePreviewLine(bridgeTeeth);
		const outerLeftId = bridge.outerLeftToothId ?? 0;
		const outerRightId = bridge.outerRightToothId ?? 0;
		const firstId = Math.min(outerLeftId, outerRightId);
		const lastId = Math.max(outerLeftId, outerRightId);

		return {
			bridgeIndex: bridge.index,
			startToothId: outerLeftId,
			endToothId: outerRightId,
			previewLine,
			previewTitle: previewLine,
			lastTouchedAt: this.maxLastTouchedAtForBridgeTeeth(bridge, toothLastTouchedAt),
			indicationId: IntuitiveIndication.Bridge,
			startToothNumberInSystem: getToothNumberById({ toothId: firstId, teethNumberingSystem }),
			endToothNumberInSystem: getToothNumberById({ toothId: lastId, teethNumberingSystem }),
			isInvalid
		};
	}

	get areImplantModalsSuppressed(): boolean {
		return !!this.rxTemplateService.currentTemplate.ui.teethDiagram.items.toothEditorProperties.hidden;
	}

	get teethRecord(): Record<number, Tooth> {
		return this.teethDiagramQuery.teethRecord;
	}

	get shouldValidateForSend(): boolean {
		return this.shellQuery.shouldValidateForSend;
	}

	constructor(
		private readonly shellQuery: ShellQuery,
		private readonly orderQuery: OrderQuery,
		private readonly teethDiagramQuery: TeethDiagramQuery,
		private readonly teethDiagramStore: TeethDiagramStore,
		private readonly intuitiveQuery: RxIntuitiveTeethDiagramQuery,
		private readonly bridgeService: RxIntuitiveBridgeService,
		private readonly hostPlatformService: HostPlatformService,
		private readonly validateRxService: ValidateRxService,
		private readonly indicationMapper: RxIntuitiveIndicationMapperService,
		private readonly logger: LoggerService,
		private readonly toothTreatmentMapper: RxIntuitiveToothTreatmentMapper,
		private readonly rxTemplateService: RxTemplateService
	) {}

	/** Gets single tooth by ID using O(1) record lookup */
	getToothById(toothId: number, teethRecord?: Record<number, Tooth>): Tooth | undefined {
		const record = teethRecord ?? this.teethRecord;

		return record[toothId];
	}

	/** Gets multiple teeth by IDs using O(1) record lookup */
	getTeethByIds(toothIds: number[], teethRecord?: Record<number, Tooth>): Tooth[] {
		const record = teethRecord ?? this.teethRecord;

		return toothIds.map(id => record[id]).filter((t): t is Tooth => t != null);
	}

	validateToothForSend(tooth: Tooth, bridge?: IntuitiveBridge): boolean {
		if (!this.shouldValidateForSend) {
			return false;
		}

		const toothIndication = this.getToothIndication(tooth, bridge);
		const isDefaultIndication = this.indicationMapper.isDefaultIndication(toothIndication);

		return isDefaultIndication ? false : this.validateRxService.isTreatmentInvalidForSend(tooth);
	}

	getToothIndication(tooth: Tooth, bridge?: IntuitiveBridge): IntuitiveIndication {
		if (!bridge) {
			return this.indicationMapper.getUnitTypeIndicationForDiagram(tooth.UnitTypeID);
		}

		const bridgeTooth = bridge.teeth.filter(t => t.ToothID === tooth.ToothID)[0];

		return this.indicationMapper.getBridgeIndicationForDiagram(bridgeTooth);
	}

	isFixedRestorativeProcedure(): boolean {
		return (this.orderQuery.procedureMap?.ProcedureId as ProcedureEnum) === ProcedureEnum.FixedRestorative;
	}

	private maxLastTouchedAtForBridgeTeeth(bridge: IntuitiveBridge, toothLastTouchedAt: Record<number, number>): number {
		let max = 0;

		for (const tooth of bridge.teeth) {
			const id = tooth.ToothID;

			if (id == null) {
				continue;
			}

			const touched = toothLastTouchedAt[id] ?? 0;

			if (touched > max) {
				max = touched;
			}
		}

		return max;
	}
}
